"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MessageEndpoint = exports.extractMessageNameFromTypeName = exports.ProcessMessageErrorReason = exports.SendMessageErrorReason = void 0;
var logging_1 = require("@augloop/logging/dist/src/client/logging");
var session_health_event_1 = require("@augloop/logging/dist/src/session-health-event");
var types_core_1 = require("@augloop/types-core");
var messages_g_1 = require("./generated/messages.g");
var sweep_cache_1 = require("@augloop/sweep-cache");
var SendMessageErrorReason;
(function (SendMessageErrorReason) {
    SendMessageErrorReason["ClientDisconnected"] = "Client disconnected.";
    SendMessageErrorReason["UnsupportedSyncMessage"] = "SyncMessages with seq = -1 are not supported anymore.";
    SendMessageErrorReason["UnexpectedSeedMessage"] = "Unexpected seed message";
    SendMessageErrorReason["SyncMessageUnsupportedBatch"] = "SyncMessage with unsupported batching.";
    SendMessageErrorReason["AnnotationTokenNotFound"] = "Token not found";
})(SendMessageErrorReason = exports.SendMessageErrorReason || (exports.SendMessageErrorReason = {}));
var ProcessMessageErrorReason;
(function (ProcessMessageErrorReason) {
    ProcessMessageErrorReason["ProvisionTokenValidationError"] = "Token provision message didn't pass token validation.";
})(ProcessMessageErrorReason = exports.ProcessMessageErrorReason || (exports.ProcessMessageErrorReason = {}));
/**
 * Produce more compact message names for metrics by stripping the boilerplate portions.
 */
function extractMessageNameFromTypeName(typeName) {
    var prefixes = ['AugLoop_Excel_Session_Protocol_', 'AugLoop_Powerpoint_Session_Protocol_', 'AugLoop_Session_Protocol_'];
    var prefixFound;
    for (var _i = 0, prefixes_1 = prefixes; _i < prefixes_1.length; _i++) {
        var prefix = prefixes_1[_i];
        if (typeName.indexOf(prefix) === 0) {
            prefixFound = prefix;
            break;
        }
    }
    if (!prefixFound) {
        return 'MalformedMessageName';
    }
    var suffix = 'Message';
    var hasSuffix = typeName.indexOf(suffix, typeName.length - suffix.length) === typeName.length - suffix.length;
    return typeName.slice(prefixFound.length, hasSuffix ? -suffix.length : undefined);
}
exports.extractMessageNameFromTypeName = extractMessageNameFromTypeName;
var messageTimeoutMs = 10 * 1000;
var sweepIntervalMs = 5000; // slow the sweeps down. Since this is used for logging, we shouldn't spend too much time sweeping.
/**
 * A symmetric endpoint that both client and server can use to send and receive messages.
 */
var MessageEndpoint = /** @class */ (function () {
    function MessageEndpoint(messageIdPrefix) {
        this.nextMessageId = 1;
        this.pendingResponseCallbacks = new sweep_cache_1.Cache({ sweepInterval: sweepIntervalMs });
        this.pendingMessageCallbacks = new sweep_cache_1.Cache({ sweepInterval: sweepIntervalMs });
        this.messageCallbacks = new Map();
        this.messageIdPrefix = messageIdPrefix;
        this.source = messageIdPrefix === 'c' ? session_health_event_1.SessionHealthSource.ClientRuntime : session_health_event_1.SessionHealthSource.Core;
        this.stats = {
            sendMessageCount: 0,
            sendMessageClientDisconnectedErrors: 0,
            sendMessageErrors: 0,
            sendMessageDurationMsMax: 0,
            processMessageCount: 0,
            processMessageProvisionTokenErrors: 0,
            processMessageErrors: 0,
            processMessageDurationMsMax: 0
        };
    }
    MessageEndpoint.prototype.setClientMetadata = function (clientMetadata) {
        this.clientMetadata = clientMetadata;
    };
    MessageEndpoint.prototype.setEgress = function (egress) {
        this.egress = egress;
    };
    MessageEndpoint.prototype.ingress = function (input, done) {
        var inputBaseTypes = types_core_1.SchemaObject.getBaseTypesFor(input);
        var inputType = types_core_1.SchemaObject.getTypeNameFor(input);
        var isResponse = inputType === messages_g_1.Response.getTypeName()
            || (inputBaseTypes && inputBaseTypes.indexOf(messages_g_1.Response.getTypeName()) >= 0);
        // This is a response to a message we sent earlier, or a new incoming message?
        if (isResponse) {
            this.processResponse(input);
            done();
        }
        else {
            this.processMessage(input, done);
        }
        // On Session Close, cancel all remaining responses
        if (inputType === messages_g_1.SessionCloseMessage.getTypeName() && !input.reconnectAllowed) {
            this.clearAllPendingResponses();
        }
    };
    MessageEndpoint.prototype.sendMessage = function (message, done, oneWay, attemptNumber) {
        var _this = this;
        if (attemptNumber === void 0) { attemptNumber = 0; }
        var logOp = new session_health_event_1.SessionHealth({
            sessionHealthEventName: 'SendMessage',
            source: this.source,
            // Assume client is at fault until proven otherwise
            reason: session_health_event_1.SessionHealthReason.Client,
            // Failed sending on client results in possibly missing input, while on the server in possibly missing output
            impact: this.source === session_health_event_1.SessionHealthSource.ClientRuntime ? session_health_event_1.SessionHealthImpact.MissingInput : session_health_event_1.SessionHealthImpact.MissingOutput,
            success: true,
            message: '',
            affectedWorkflows: ['All'],
            cv: message.cv,
            resourceId: extractMessageNameFromTypeName(types_core_1.SchemaObject.getTypeNameFor(message)),
            dimension0: attemptNumber.toString()
        }).start();
        var logEvent = function () {
            logOp.setClientMetadata(_this.clientMetadata);
            if (logOp.success) {
                logging_1.Log.info(0x2224c8a0 /* tag_8jm86 */, logging_1.LogCategory.CoreDefault, logOp.stop());
            }
            else {
                // We are interested if error which we detected here wasn't successfully propagated to done callback. If so, let's report that fact so we can take this instance of session health event into account.
                var errorNotPropagatedToDoneCallback = logOp.resultSignature === 'ErrorWithoutPendingResponse' || logOp.resultSignature === 'ResponseCallbackException' || logOp.message !== 'We called into done callback';
                logOp.message = JSON.stringify({ errorNotPropagatedToDoneCallback: errorNotPropagatedToDoneCallback });
                logging_1.Log.error(0x2224c8a1 /* tag_8jm87 */, logging_1.LogCategory.CoreDefault, logOp.stop());
            }
        };
        message.messageId = "" + this.messageIdPrefix + this.nextMessageId++;
        // All non-close messages produce a response, unless the caller explicitly wants this to be a one-way message
        var isSessionCloseMessage = types_core_1.SchemaObject.getTypeNameFor(message) === messages_g_1.SessionCloseMessage.getTypeName();
        var responseExpected = !oneWay && !isSessionCloseMessage;
        if (responseExpected) {
            var onResponseInternal_1 = function (error, response) {
                var _a;
                if (error) {
                    logOp.success = false;
                    logOp.resultSignature = error.error;
                    logOp.resultDescription = "Error for " + types_core_1.SchemaObject.getTypeNameFor(message) + " message " + message.messageId + ": " + error.error;
                }
                else {
                    logOp.resultSignature = 'Success';
                }
                if (done) {
                    try {
                        logOp.message = 'We called into done callback';
                        done(error, response);
                    }
                    catch (e) {
                        logOp.success = false;
                        logOp.resultSignature = 'ResponseCallbackException';
                        logOp.resultDescription = ((_a = _this.clientMetadata) === null || _a === void 0 ? void 0 : _a.appPlatform) === 'Web'
                            ? JSON.stringify({ message: e.message, stack: e.stack })
                            : JSON.stringify({ message: e.message }); // avoid logging stack in non-web cases in case of PII
                    }
                }
                _this.pendingMessageCallbacks.del(message.messageId);
                logEvent();
                _this.updateSendMessageStats(logOp.success, logOp.durationMs, error ? new Error(error.error) : undefined);
            };
            var onTimeout = function () {
                onResponseInternal_1(new messages_g_1.TimeoutErrorResponse({ error: 'Timeout waiting for response' }), undefined);
            };
            this.pendingResponseCallbacks.put(message.messageId, onResponseInternal_1, messageTimeoutMs, onTimeout);
        }
        this.egress(message, function (error) {
            var _a, _b;
            if (error) {
                var pendingResponse = _this.pendingResponseCallbacks.get(message.messageId);
                if (pendingResponse) {
                    _this.pendingResponseCallbacks.del(message.messageId);
                    pendingResponse(new messages_g_1.ErrorResponse({ error: error.message }));
                }
                else if (isSessionCloseMessage) {
                    logOp.success = (_b = (_a = error.message) === null || _a === void 0 ? void 0 : _a.endsWith('Client disconnected.')) !== null && _b !== void 0 ? _b : false; // Client being already disconnected when sending session close message is not a failure
                    logOp.resultSignature = 'ErrorSendingSessionCloseMessage';
                    logOp.resultDescription = "Error for " + types_core_1.SchemaObject.getTypeNameFor(message) + " message " + message.messageId + ": " + error.message;
                    logEvent();
                }
                else {
                    logOp.success = false;
                    logOp.resultSignature = 'ErrorWithoutPendingResponse';
                    logOp.resultDescription = "Error for " + types_core_1.SchemaObject.getTypeNameFor(message) + " message " + message.messageId + ": " + error.message;
                    logEvent();
                }
            }
        });
    };
    MessageEndpoint.prototype.onMessage = function (messageType, onMessage) {
        this.messageCallbacks.set(messageType, onMessage);
    };
    MessageEndpoint.prototype.getStats = function () {
        return this.stats;
    };
    MessageEndpoint.prototype.hasMessageCallback = function (messageType) {
        return this.messageCallbacks.has(messageType);
    };
    MessageEndpoint.prototype.cancelPendingResponseCallbacks = function () {
        var _this = this;
        this.pendingResponseCallbacks.forEach(function (pendingResponse, key) {
            if (pendingResponse) {
                _this.pendingResponseCallbacks.del(key);
                pendingResponse(new messages_g_1.ErrorResponse({ error: 'Cancelled' }));
            }
        });
    };
    MessageEndpoint.prototype.clearAllPendingResponses = function () {
        if (this.pendingResponseCallbacks.size() == 0) {
            return;
        }
        var logOp = new logging_1.Operation({
            operationName: 'PurgePendingResponses',
            resultDescription: this.pendingResponseCallbacks.size().toString(),
            success: true
        });
        logging_1.Log.info(0x2224c8a2 /* tag_8jm88 */, logging_1.LogCategory.CoreDefault, logOp);
        this.pendingResponseCallbacks.clear();
    };
    MessageEndpoint.prototype.processMessage = function (message, done) {
        var _this = this;
        var _a;
        var logOp = new session_health_event_1.SessionHealth({
            sessionHealthEventName: 'ProcessMessage',
            source: this.source,
            // Assume client is at fault until proven otherwise
            reason: session_health_event_1.SessionHealthReason.Client,
            // Failed processing on client results in possibly missing annotations, while on the server in possibly missing input
            impact: this.source === session_health_event_1.SessionHealthSource.ClientRuntime ? session_health_event_1.SessionHealthImpact.MissingOutput : session_health_event_1.SessionHealthImpact.MissingInput,
            success: true,
            message: '',
            affectedWorkflows: ['All'],
            cv: message.cv
        }).start();
        var logEvent = function () {
            logOp.setClientMetadata(_this.clientMetadata);
            if (logOp.success) {
                logging_1.Log.info(0x2224c8a3 /* tag_8jm89 */, logging_1.LogCategory.CoreDefault, logOp.stop());
            }
            else {
                // We are interested if error which we detected here wasn't detectable by registered messageCallback. If so, let's report that fact so we can take this instance of session health event into account.
                var errorHappenedOutsideRegisteredMessageCallback = logOp.resourceId === 'UnsupportedMessage' || logOp.resultSignature === 'Timeout' || logOp.resultSignature === 'OnResponseInvokedMoreThanOnce' || logOp.resultSignature === 'MessageCallbackException' || logOp.message !== 'We called into messageCallback';
                logOp.message = JSON.stringify({ errorHappenedOutsideRegisteredMessageCallback: errorHappenedOutsideRegisteredMessageCallback });
                logging_1.Log.error(0x2224c8c0 /* tag_8jm9a */, logging_1.LogCategory.CoreDefault, logOp.stop());
            }
        };
        var messageCallback = this.messageCallbacks.get(types_core_1.SchemaObject.getTypeNameFor(message));
        if (messageCallback) {
            // Don't set the resourceId until we've confirmed it's a known message type
            // (to avoid potential cardinality explosion in metrics)
            logOp.resourceId = extractMessageNameFromTypeName(types_core_1.SchemaObject.getTypeNameFor(message));
        }
        else {
            messageCallback = function (_message, sendResponse) {
                logOp.resourceId = 'UnsupportedMessage';
                sendResponse(new messages_g_1.ErrorResponse({ error: "Message type " + types_core_1.SchemaObject.getTypeNameFor(message) + " is not supported" }));
            };
        }
        var onTimeout = function () {
            logOp.success = false;
            logOp.resultSignature = 'Timeout';
            logOp.resultDescription = 'Timeout processing message callback';
            logEvent();
            logging_1.Log.error(0x2224c8c1 /* tag_8jm9b */, logging_1.LogCategory.CoreDefault, "Timeout processing " + logOp.resourceId);
            _this.updateProcessMessageStats(false, logOp.durationMs, new Error('timeout'));
        };
        this.pendingMessageCallbacks.put(message.messageId, undefined, messageTimeoutMs, onTimeout);
        var onResponseInvoked = false;
        var onResponse = function (error, response) {
            _this.pendingMessageCallbacks.del(message.messageId);
            if (onResponseInvoked) {
                logOp.success = false;
                logOp.resultSignature = 'OnResponseInvokedMoreThanOnce';
                logOp.resultDescription = "Invoked onResponse for " + types_core_1.SchemaObject.getTypeNameFor(message) + " message " + message.messageId + " more than once";
            }
            else if (error) {
                logOp.success = false;
                logOp.resultSignature = error.error;
                logOp.resultDescription = "Error for " + types_core_1.SchemaObject.getTypeNameFor(message) + " message " + message.messageId + ": " + error.error;
            }
            else {
                logOp.resultSignature = 'Success';
            }
            onResponseInvoked = true;
            logEvent();
            // Enforce this is an ErrorResponse
            if (error && !types_core_1.SchemaObject.matchesTypesFor(error, [messages_g_1.ErrorResponse.getTypeName()])) {
                error = new messages_g_1.ErrorResponse({ error: 'Internal Server Error' });
            }
            _this.updateProcessMessageStats(logOp.success, logOp.durationMs, error ? new Error(error.error) : undefined);
            if (error) {
                error.messageId = message.messageId;
                done(error);
            }
            else if (response) {
                response.messageId = message.messageId;
                done(undefined, response);
            }
            else {
                done();
            }
        };
        try {
            logOp.message = 'We called into messageCallback';
            messageCallback(message, onResponse);
        }
        catch (e) {
            this.pendingMessageCallbacks.del(message.messageId);
            logOp.success = false;
            logOp.resultSignature = 'MessageCallbackException';
            logOp.resultDescription = ((_a = this.clientMetadata) === null || _a === void 0 ? void 0 : _a.appPlatform) === 'Web'
                ? JSON.stringify({ message: e.message, stack: e.stack })
                : JSON.stringify({ message: e.message }); // avoid logging stack in non-web cases in case of PII
            logEvent();
            this.updateProcessMessageStats(false, logOp.durationMs, e);
            done();
        }
    };
    MessageEndpoint.prototype.processResponse = function (response) {
        var _a;
        var logOp = new logging_1.Operation({
            operationName: 'ProcessResponse'
        });
        logOp.success = true;
        logOp.setClientMetadata(this.clientMetadata);
        logOp.start();
        var pendingResponse = this.pendingResponseCallbacks.get(response.messageId);
        if (pendingResponse) {
            this.pendingResponseCallbacks.del(response.messageId);
            if (types_core_1.SchemaObject.matchesTypesFor(response, [messages_g_1.ErrorResponse.getTypeName()])) {
                pendingResponse(response);
            }
            else {
                pendingResponse(undefined, response);
            }
        }
        else {
            logOp.resultSignature = 'NoPendingMessage';
            logOp.resultDescription = "" + response.messageId;
            logOp.success = false;
        }
        // This operation is very high-volume, even when aggregated, so only log it for inner-ring clients
        if (((_a = this.clientMetadata) === null || _a === void 0 ? void 0 : _a.releaseAudienceGroup) !== 'Production') {
            logOp.resourceId = extractMessageNameFromTypeName(types_core_1.SchemaObject.getTypeNameFor(response));
            logging_1.Log.info(0x2224c8c2 /* tag_8jm9c */, logging_1.LogCategory.CoreDefault, logOp.stop());
        }
    };
    MessageEndpoint.prototype.updateSendMessageStats = function (success, durationMs, error) {
        this.stats.sendMessageCount++;
        this.stats.sendMessageDurationMsMax = Math.max(durationMs, this.stats.sendMessageDurationMsMax);
        if (!success && !error) {
            logging_1.Log.warn(0x2224c8c3 /* tag_8jm9d */, logging_1.LogCategory.CoreDefault, 'Failed send message did not provide error object.');
        }
        else if (success && error) {
            logging_1.Log.warn(0x2224c8c4 /* tag_8jm9e */, logging_1.LogCategory.CoreDefault, 'Succeeded send message provided error object.');
        }
        if (success) {
            return;
        }
        if (error && error.message === SendMessageErrorReason.ClientDisconnected) {
            this.stats.sendMessageClientDisconnectedErrors++;
        }
        else {
            this.stats.sendMessageErrors++;
        }
    };
    MessageEndpoint.prototype.updateProcessMessageStats = function (success, durationMs, error) {
        this.stats.processMessageCount++;
        this.stats.processMessageDurationMsMax = Math.max(durationMs, this.stats.processMessageDurationMsMax);
        if (!success && !error) {
            logging_1.Log.warn(0x2224c8c5 /* tag_8jm9f */, logging_1.LogCategory.CoreDefault, 'Failed process message did not provide error object.');
        }
        else if (success && error) {
            logging_1.Log.warn(0x2224c8c6 /* tag_8jm9g */, logging_1.LogCategory.CoreDefault, 'Succeeded process message provided error object.');
        }
        if (success) {
            return;
        }
        if (error && error.message === ProcessMessageErrorReason.ProvisionTokenValidationError) {
            this.stats.processMessageProvisionTokenErrors++;
        }
        else {
            this.stats.processMessageErrors++;
        }
    };
    return MessageEndpoint;
}());
exports.MessageEndpoint = MessageEndpoint;
//# sourceMappingURL=message-endpoint.js.map